export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
const MEDIA_HOST = API_BASE_URL.replace(/\/api\/?$/, '');

/** Public website (risp-frontend) origin — used to link out to family-facing pages. */
export const FRONTEND_URL = import.meta.env.VITE_FRONTEND_URL || 'http://localhost:10811';

/**
 * Resolve a stored media path (e.g. `/uploads/news/img-X.webp`) to an absolute URL on the API host.
 *
 * `blob:` and `data:` values pass through unchanged alongside `http(s):` ones — they're client-side
 * URLs the browser can already load (e.g. an in-progress crop preview from `URL.createObjectURL`),
 * and prefixing the API host onto one would only break it.
 */
export function mediaUrl(path: string | null | undefined): string {
  if (!path) return '';
  if (/^(?:https?|blob|data):/i.test(path)) return path;
  if (path.startsWith('/')) return `${MEDIA_HOST}${path}`;
  return `${MEDIA_HOST}/${path}`;
}

/**
 * How long a request may run before it is aborted. Without this, `fetch` waits
 * indefinitely: a stalled API leaves the caller's mutation permanently pending,
 * which surfaces as a dialog stuck on "Saving..." with no error and no way out.
 * Callers doing genuinely long work (imports, bulk exports) pass a larger value.
 */
const DEFAULT_TIMEOUT_MS = 30_000;

export type FetchApiOptions = RequestInit & { timeoutMs?: number };

export class RequestTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`The server did not respond within ${Math.round(timeoutMs / 1000)}s. Please try again.`);
    this.name = 'RequestTimeoutError';
  }
}

/** Thrown by fetchApi on a non-2xx response, carrying the HTTP status alongside the message —
 *  needed by callers whose API partitions failures by status code (e.g. translation's
 *  412/422/502/503, each meaning a different thing to an operator) rather than by message text. */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    /** The parsed error body, for endpoints that explain a 400 with more than a message. */
    public readonly body?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Everything up to a successful response: timeout, credentials, and the 401 bounce to /login.
 *
 * Shared so `fetchApi` and `fetchApiBlob` cannot drift on any of it — a download path that
 * quietly stopped redirecting an expired session would leave the user staring at a corrupt file
 * instead of a login page.
 */
async function requestApi(endpoint: string, options?: FetchApiOptions): Promise<Response> {
  const url = `${API_BASE_URL}${endpoint}`;

  const { timeoutMs = DEFAULT_TIMEOUT_MS, signal, ...init } = options ?? {};
  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  // Honour a caller-supplied signal (e.g. react-query cancellation) alongside the timeout.
  const combinedSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;

  const isFormData = init.body instanceof FormData;
  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      signal: combinedSignal,
      credentials: 'include',
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...init.headers,
      },
    });
  } catch (err) {
    // Distinguish "we gave up waiting" from a caller-initiated cancellation, so the
    // UI can show something actionable instead of a generic abort.
    if (timeoutSignal.aborted) throw new RequestTimeoutError(timeoutMs);
    throw err;
  }

  if (!response.ok) {
    if (response.status === 401 && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new ApiError(error.message || `API error: ${response.status}`, response.status, error);
  }

  return response;
}

export async function fetchApi<T>(endpoint: string, options?: FetchApiOptions): Promise<T> {
  const response = await requestApi(endpoint, options);

  /**
   * An empty body is not always a 204. A Nest handler returning `Promise<void>` answers a PATCH
   * or POST with 200 and nothing in it, and `response.json()` on that throws "Unexpected end of
   * JSON input" — a write that succeeded, reported to the office as a failure. Checking the
   * status alone caught only the endpoints that had been changed to answer 204; reading the body
   * first covers every endpoint that returns nothing, which is most of the office's actions.
   */
  if (response.status === 204) return undefined as T;
  const body = await response.text();
  if (body === '') return undefined as T;
  return JSON.parse(body) as T;
}

/**
 * Fetch a stored media path as a blob — for saving a file the browser already displays.
 *
 * Separate from `fetchApiBlob` because a media path is already rooted at `/api/...`, so it is
 * resolved through `mediaUrl` rather than appended to the API base. Credentials are sent because
 * private uploads are auth-gated: without them this returns a 401 body that would otherwise be
 * saved to disk as if it were the image.
 */
export async function fetchMediaBlob(path: string): Promise<Blob> {
  const response = await fetch(mediaUrl(path), { credentials: 'include' });
  if (!response.ok) throw new Error(`Could not download the file (${response.status})`);
  return response.blob();
}

/** Same request handling, for endpoints that answer with a file rather than JSON. */
export async function fetchApiBlob(endpoint: string, options?: FetchApiOptions): Promise<Blob> {
  const response = await requestApi(endpoint, options);
  return response.blob();
}
